import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../api';
import type { CanonEntry, LifeEvent } from '../types';
import { formatDate } from '../format';
import { Button, Empty, ErrorNotice, Panel, Skeleton, Status } from '../ui';

/** Local loader so this module stays independent from pages.tsx internals. */
export function usePanel<T>(loader: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    loader()
      .then((value) => { if (alive) setData(value); })
      .catch((caught) => { if (alive) setError(errorMessage(caught)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [loader, tick]);
  return { data, error, loading, reload: () => setTick((value) => value + 1) };
}

const CANON_TRUTH: Record<string, string> = {
  user_authored_fictional: 'خلفية قصصية من تأليف المالك',
  canonical: 'حقيقة ثابتة معتمدة',
  observed_event: 'حدث وقع داخل رامي',
  user_confirmed: 'معلومة أكدها فارس'
};
const CANON_STATUS: Record<string, string> = {
  pending_owner: 'بانتظار قرار المالك',
  canonical: 'معتمد',
  rejected: 'مرفوض',
  superseded: 'مستبدَل'
};
const LIFE_KIND_LABELS: Record<string, string> = {
  travel: 'سفر', job_change: 'تغير عمل', relocation: 'انتقال',
  education: 'تعليم', routine: 'روتين', other: 'أخرى'
};
const LIFE_STATE_LABELS: Record<string, string> = {
  proposed: 'مقترح', scheduled: 'مجدول', active: 'جارٍ',
  completed: 'مكتمل', cancelled: 'ملغى', rejected: 'مرفوض'
};
const POLICY_LABELS: Record<string, string> = {
  automatic: 'تلقائي', requires_owner: 'يحتاج قرار المالك', prohibited: 'ممنوع'
};
const DEFAULT_POLICY: Record<string, string> = {
  travel: 'requires_owner', job_change: 'requires_owner', relocation: 'requires_owner',
  education: 'requires_owner', routine: 'automatic', other: 'requires_owner'
};

export type GrowthView = {
  lockedTraits?: string[];
  allowedTraits?: string[];
  autoPersistStories?: boolean;
  majorEventPolicy?: Record<string, string>;
};

function splitList(value: string): string[] {
  return value.split(',').map((item) => item.trim()).filter((item) => item.length > 0);
}

/** Shared owner path: draft → structural validation → diff preview → activate a new version. */
async function applyOwnerDraft(personaId: string, fields: Record<string, unknown>, versionId?: string): Promise<boolean> {
  const draftVersion = await api.personaDraft(personaId, fields, 'تعديل من لوحة المالك', versionId);
  if (draftVersion.rejectedFields.length) throw new Error('حقول غير مصرح بها: ' + draftVersion.rejectedFields.join(', '));
  const diff = await api.personaDiff(personaId, draftVersion.versionId);
  const summary = diff.changes.map((change) => change.field + ': ' + JSON.stringify(change.from) + ' → ' + JSON.stringify(change.to)).join('\n');
  if (!confirm('معاينة الفروق قبل التفعيل:\n' + (summary || 'لا فروق') + '\n\nهل تريد تفعيل الإصدار ' + draftVersion.version + '؟')) return false;
  await api.personaActivate(personaId, draftVersion.versionId, 'تفعيل بعد معاينة الفروق', versionId);
  return true;
}

export function ConversationPreferencesPanel() {
  const loader = useCallback(() => api.personaPreferences(), []);
  const { data, error, loading, reload } = usePanel(loader);
  const [replyMode, setReplyMode] = useState<'text' | 'voice_note'>('text');
  const [outgoingImageMode, setOutgoingImageMode] = useState<'generate' | 'internet' | 'off'>('off');
  const [ambientEnabled, setAmbientEnabled] = useState(false);
  const [ambientLevel, setAmbientLevel] = useState(0.15);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    if (!data) return;
    setReplyMode(data.replyMode === 'voice_note' ? 'voice_note' : 'text');
    setOutgoingImageMode(data.outgoingImageMode === 'generate' ? 'generate' : data.outgoingImageMode === 'internet' ? 'internet' : 'off');
    setAmbientEnabled(data.ambientEnabled === true);
    setAmbientLevel(Number.isFinite(data.ambientLevel) ? data.ambientLevel : 0.15);
  }, [data]);
  async function save() {
    setSaving(true); setNotice('');
    try {
      await api.savePersonaPreferences({ replyMode, outgoingImageMode, ambientEnabled, ambientLevel });
      setNotice('حُفظت تفضيلات المحادثة.');
      reload();
    } catch (caught) { setNotice(errorMessage(caught)); } finally { setSaving(false); }
  }
  return <Panel title="أسلوب الرد والوسائط" intro="افتراضي الجلسات الجديدة؛ لا يفعّل مزودًا حقيقيًا من تلقاء نفسه.">
    {error ? <ErrorNotice message={error} onRetry={reload} /> : loading ? <Skeleton /> : <div className="form-grid">
      <label>نمط الرد الافتراضي<select value={replyMode} onChange={(event) => setReplyMode(event.target.value === 'voice_note' ? 'voice_note' : 'text')}><option value="text">نص</option><option value="voice_note">رسالة صوتية</option></select></label>
      <label>الصور الصادرة<select value={outgoingImageMode} onChange={(event) => setOutgoingImageMode(event.target.value === 'generate' ? 'generate' : event.target.value === 'internet' ? 'internet' : 'off')}><option value="off">معطّل</option><option value="generate">توليد (ينتظر مزودًا)</option><option value="internet">بحث إنترنت (ينتظر مزودًا)</option></select></label>
      <label className="check-row"><input type="checkbox" checked={ambientEnabled} onChange={(event) => setAmbientEnabled(event.target.checked)} /><span>الصوت المحيط داخل المحادثة</span></label>
      <label>مستوى الصوت المحيط ({Math.round(ambientLevel * 100)}٪)<input type="range" min={0} max={1} step={0.05} value={ambientLevel} onChange={(event) => setAmbientLevel(Number(event.target.value))} /></label>
      <Button type="button" busy={saving} onClick={() => void save()}>حفظ تفضيلات المحادثة</Button>
      {notice ? <p className="muted" role="status">{notice}</p> : null}
    </div>}
  </Panel>;
}

export function GrowthPanel({ personaId, versionId, growth, onSaved }: {
  personaId: string; versionId?: string | undefined; growth: GrowthView | null; onSaved: () => void;
}) {
  const [locked, setLocked] = useState((growth?.lockedTraits ?? []).join(', '));
  const [allowed, setAllowed] = useState((growth?.allowedTraits ?? []).join(', '));
  const [autoPersist, setAutoPersist] = useState(growth?.autoPersistStories !== false);
  const [policy, setPolicy] = useState<Record<string, string>>({ ...DEFAULT_POLICY, ...(growth?.majorEventPolicy ?? {}) });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    setLocked((growth?.lockedTraits ?? []).join(', '));
    setAllowed((growth?.allowedTraits ?? []).join(', '));
    setAutoPersist(growth?.autoPersistStories !== false);
    setPolicy({ ...DEFAULT_POLICY, ...(growth?.majorEventPolicy ?? {}) });
  }, [growth]);
  async function save() {
    setSaving(true); setNotice('');
    try {
      const applied = await applyOwnerDraft(personaId, {
        'growth.lockedTraits': splitList(locked),
        'growth.allowedTraits': splitList(allowed),
        'growth.autoPersistStories': autoPersist,
        'growth.majorEventPolicy': policy
      }, versionId);
      if (applied) { setNotice('حُفظ إصدار جديد وفُعّل.'); onSaved(); }
    } catch (caught) { setNotice(errorMessage(caught)); } finally { setSaving(false); }
  }
  return <Panel title="حدود النمو والثوابت المتغيرة" intro="ما يُسمح للنموذج بتطويره وما يبقى مقفلًا بيد المالك فقط.">
    <div className="form-grid">
      <label className="full">سمات مقفلة (لا يغيّرها النموذج)<input value={locked} onChange={(event) => setLocked(event.target.value)} placeholder="curiosity, humor" /></label>
      <label className="full">سمات مسموح بتطورها<input value={allowed} onChange={(event) => setAllowed(event.target.value)} placeholder="vocabulary, interests" /></label>
      <label className="check-row"><input type="checkbox" checked={autoPersist} onChange={(event) => setAutoPersist(event.target.checked)} /><span>حفظ القصص السردية دون مراجعة</span></label>
      <p className="muted">سياسة الأحداث المهمة</p>
      <div className="card-list">{Object.keys(DEFAULT_POLICY).map((kind) => <article key={kind} className="row-between">
        <span>{LIFE_KIND_LABELS[kind] ?? kind}</span>
        <select value={policy[kind] ?? 'requires_owner'} onChange={(event) => setPolicy((current) => ({ ...current, [kind]: event.target.value }))}>
          <option value="automatic">تلقائي</option>
          <option value="requires_owner">يحتاج قرار المالك</option>
          <option value="prohibited">ممنوع</option>
        </select>
      </article>)}</div>
      <Button type="button" busy={saving} onClick={() => void save()}>معاينة الفرق وحفظ إصدار</Button>
      {notice ? <p className="muted" role="status">{notice}</p> : null}
    </div>
  </Panel>;
}

export function CanonPanel({ personaId }: { personaId: string }) {
  const loader = useCallback(() => api.canonEntries(personaId), [personaId]);
  const { data, error, loading, reload } = usePanel(loader);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setSaving(true); setNotice('');
    try {
      const result = await api.submitCanon(personaId, {
        title: String(form.get('title') ?? '').trim(),
        body: String(form.get('body') ?? '').trim(),
        ...(String(form.get('occurredAt') ?? '') ? { occurredAt: String(form.get('occurredAt')) } : {}),
        ...(String(form.get('location') ?? '').trim() ? { location: String(form.get('location')).trim() } : {}),
        ...(String(form.get('sourceConversationId') ?? '').trim() ? { sourceConversationId: String(form.get('sourceConversationId')).trim() } : {}),
        tense: form.get('tense') === 'plan' ? 'plan' : 'completed',
        requireOwnerApproval: form.get('requireOwnerApproval') === 'on'
      });
      setNotice(result.conflicts.length ? 'رُفض عنصر السرد: ' + result.conflicts.join(', ') : 'أُضيف عنصر سردي (' + result.status + ').');
      formElement.reset();
      reload();
    } catch (caught) { setNotice(errorMessage(caught)); } finally { setSaving(false); }
  }
  async function decide(id: string, decision: 'approve' | 'reject') {
    setSaving(true); setNotice('');
    try { await api.decideCanon(personaId, id, decision); setNotice(decision === 'approve' ? 'اعتُمد العنصر السردي.' : 'رُفض العنصر السردي.'); reload(); }
    catch (caught) { setNotice(errorMessage(caught)); } finally { setSaving(false); }
  }
  const items = data?.items ?? [];
  return <Panel title="كانون السرد" intro="خلفية قصصية/أحداث روائية مصنّفة بوضوح. ذكر فارس مسموح فقط عند ربطه بمحادثة حقيقية موثقة.">
    <form className="form-grid" onSubmit={submit}>
      <label className="full">العنوان<input name="title" required maxLength={120} /></label>
      <label className="full">النص<textarea name="body" rows={3} required /></label>
      <label>التاريخ (اختياري)<input name="occurredAt" type="date" /></label>
      <label>المكان (اختياري)<input name="location" maxLength={120} /></label>
      <label>صيغة الزمن<select name="tense" defaultValue="completed"><option value="completed">حدث مكتمل</option><option value="plan">خطة مستقبلية</option></select></label>
      <label className="full">معرف محادثة المصدر عند ذكر فارس (اختياري)<input name="sourceConversationId" dir="ltr" placeholder="UUID" /></label>
      <label className="check-row"><input type="checkbox" name="requireOwnerApproval" defaultChecked /><span>يتطلب اعتماد المالك قبل التفعيل</span></label>
      <Button type="submit" busy={saving}>إضافة عنصر سردي</Button>
      {notice ? <p className="muted" role="status">{notice}</p> : null}
    </form>
    {error ? <ErrorNotice message={error} onRetry={reload} /> : loading ? <Skeleton /> : items.length ? <div className="card-list">{items.map((entry: CanonEntry) => <article key={entry.id}>
      <div className="row-between"><strong>{entry.title}</strong><Status tone={entry.status === 'canonical' ? 'good' : entry.status === 'rejected' ? 'bad' : 'warning'}>{CANON_STATUS[entry.status] ?? entry.status}</Status></div>
      <p>{entry.body}</p>
      <p className="muted">تصنيف: {CANON_TRUTH[entry.truthClass] ?? entry.truthClass} — {entry.occurredAt ? formatDate(entry.occurredAt) : 'بلا تاريخ'}{entry.location ? ' — ' + entry.location : ''}</p>
      {entry.status === 'pending_owner' ? <div className="inline-actions"><Button variant="secondary" busy={saving} onClick={() => void decide(entry.id, 'approve')}>اعتماد</Button><Button variant="danger" busy={saving} onClick={() => void decide(entry.id, 'reject')}>رفض</Button></div> : null}
    </article>)}</div> : <Empty title="لا عناصر سردية بعد" />}
  </Panel>;
}

export function LifeEventsPanel({ personaId }: { personaId: string }) {
  const loader = useCallback(() => api.lifeEvents(personaId), [personaId]);
  const { data, error, loading, reload } = usePanel(loader);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  async function add(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setSaving(true); setNotice('');
    try {
      const result = await api.recordLifeEvent(personaId, {
        kind: String(form.get('kind') ?? 'other'),
        summary: String(form.get('summary') ?? '').trim(),
        ...(String(form.get('location') ?? '').trim() ? { location: String(form.get('location')).trim() } : {}),
        ...(String(form.get('startsAt') ?? '') ? { startsAt: String(form.get('startsAt')) } : {}),
        ...(String(form.get('endsAt') ?? '') ? { endsAt: String(form.get('endsAt')) } : {})
      });
      setNotice('أُضيف حدث حياة (' + result.state + ') وفق سياسة ' + (POLICY_LABELS[result.policy] ?? result.policy) + '.');
      formElement.reset();
      reload();
    } catch (caught) { setNotice(errorMessage(caught)); } finally { setSaving(false); }
  }
  async function decide(id: string, decision: 'approve' | 'reject' | 'complete' | 'cancel') {
    setSaving(true); setNotice('');
    try { await api.decideLifeEvent(personaId, id, decision); setNotice('حُدّث حدث الحياة.'); reload(); }
    catch (caught) { setNotice(errorMessage(caught)); } finally { setSaving(false); }
  }
  async function catchUp() {
    setSaving(true); setNotice('');
    try {
      const result = await api.lifeCatchUp(personaId);
      setNotice('مزامنة الزمن: ' + result.transitions + ' انتقالًا' + (result.needsProvider ? ' — بعض العناصر تنتظر مزودًا.' : '.'));
      reload();
    } catch (caught) { setNotice(errorMessage(caught)); } finally { setSaving(false); }
  }
  const items = data?.items ?? [];
  return <Panel title="تاريخ النمو وأحداث الحياة" intro="أحداث حقيقية داخل رامي، مربوطة بسياسة ومعاينة قبل التفعيل." actions={<Button variant="secondary" busy={saving} onClick={() => void catchUp()}>مزامنة الزمن المنقضي</Button>}>
    <form className="form-grid" onSubmit={add}>
      <label>النوع<select name="kind" defaultValue="other">{Object.entries(LIFE_KIND_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <label className="full">الملخص<input name="summary" required maxLength={200} /></label>
      <label>المكان (اختياري)<input name="location" maxLength={120} /></label>
      <label>يبدأ (اختياري)<input name="startsAt" type="date" /></label>
      <label>ينتهي (اختياري)<input name="endsAt" type="date" /></label>
      <Button type="submit" busy={saving}>تسجيل حدث حياة</Button>
      {notice ? <p className="muted" role="status">{notice}</p> : null}
    </form>
    {error ? <ErrorNotice message={error} onRetry={reload} /> : loading ? <Skeleton /> : items.length ? <div className="card-list">{items.map((entry: LifeEvent) => <article key={entry.id}>
      <div className="row-between"><strong>{entry.summary}</strong><Status tone={entry.state === 'completed' ? 'good' : entry.state === 'cancelled' || entry.state === 'rejected' ? 'bad' : 'warning'}>{LIFE_STATE_LABELS[entry.state] ?? entry.state}</Status></div>
      <p className="muted">{LIFE_KIND_LABELS[entry.kind] ?? entry.kind} — السياسة: {POLICY_LABELS[entry.policy] ?? entry.policy}{entry.location ? ' — ' + entry.location : ''} — {formatDate(entry.updatedAt)}</p>
      <div className="inline-actions">
        <Button variant="secondary" busy={saving} onClick={() => void decide(entry.id, 'approve')}>اعتماد</Button>
        <Button variant="secondary" busy={saving} onClick={() => void decide(entry.id, 'complete')}>إكمال</Button>
        <Button variant="danger" busy={saving} onClick={() => void decide(entry.id, 'cancel')}>إلغاء</Button>
        <Button variant="danger" busy={saving} onClick={() => void decide(entry.id, 'reject')}>رفض</Button>
      </div>
    </article>)}</div> : <Empty title="لا أحداث حياة بعد" />}
  </Panel>;
}

/** Composite block dropped into PersonaPage; keeps pages.tsx free of dense inline JSX. */
export function PersonaControlPanels({ personaId, document, versionId, onChanged }: {
  personaId: string;
  document: Record<string, unknown> | null;
  versionId?: string | undefined;
  onChanged: () => void;
}) {
  const growth = (document?.['growth'] ?? null) as GrowthView | null;
  const pending = (document?.['pendingOwnerDecision'] ?? []) as string[];
  return <>
    <ConversationPreferencesPanel />
    <GrowthPanel personaId={personaId} versionId={versionId} growth={growth} onSaved={onChanged} />
    <CanonPanel personaId={personaId} />
    <LifeEventsPanel personaId={personaId} />
    {pending.length ? <Panel title="قرارات مالك معلقة" intro="حقول لم يحسمها المالك بعد؛ لا يخترعها النموذج."><div className="card-list">{pending.map((item) => <article key={item}>{item}</article>)}</div></Panel> : null}
  </>;
}
